import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Loading } from '@carbon/react';
import { AppLayout } from '@/layouts/AppLayout';
import PortalLayout from '@/layouts/PortalLayout';
import { ProtectedRoute } from '@/layouts/ProtectedRoute';
import { queryClient } from '@/lib/queryClient';
import { useAuthStore } from '@/stores/auth.store';

function PortalGuard() {
  const token = localStorage.getItem('portal_token');
  if (!token) return <Navigate to="/portal/login" replace />;
  return <Outlet />;
}

function CockpitRoute({ element }: { element: React.ReactElement }) {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== 'ADMIN' && role !== 'MANAGER') {
    return <Navigate to="/" replace />;
  }
  return element;
}
import '@carbon/styles/css/styles.css';
import '@/styles/elbahi.css';

const Login = lazy(() => import('@/pages/auth/Login'));
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'));
const ContactList = lazy(() => import('@/pages/contacts/ContactList'));
const ContactDetail = lazy(() => import('@/pages/contacts/ContactDetail'));
const QuoteList = lazy(() => import('@/pages/quotes/QuoteList'));
const QuoteForm = lazy(() => import('@/pages/quotes/QuoteForm'));
const QuoteDetail = lazy(() => import('@/pages/quotes/QuoteDetail'));
const InvoiceList = lazy(() => import('@/pages/invoices/InvoiceList'));
const InvoiceDetail = lazy(() => import('@/pages/invoices/InvoiceDetail'));
const EmployeeList = lazy(() => import('@/pages/hr/EmployeeList'));
const EmployeeDetail = lazy(() => import('@/pages/hr/EmployeeDetail'));
const LeaveList = lazy(() => import('@/pages/hr/LeaveList'));
const TimesheetWeek = lazy(() => import('@/pages/hr/TimesheetWeek'));
const PayrollList = lazy(() => import('@/pages/hr/PayrollList'));
const BankList = lazy(() => import('@/pages/banks/BankList'));
const BankDetail = lazy(() => import('@/pages/banks/BankDetail'));
const ProjectList = lazy(() => import('@/pages/projects/ProjectList'));
const ProjectDetail = lazy(() => import('@/pages/projects/ProjectDetail'));
const TicketList = lazy(() => import('@/pages/helpdesk/TicketList'));
const TicketDetail = lazy(() => import('@/pages/helpdesk/TicketDetail'));
const AIAssistant = lazy(() => import('@/pages/ai/AIAssistant'));
const ContractList = lazy(() => import('@/pages/contracts/ContractList'));
const ContractDetail = lazy(() => import('@/pages/contracts/ContractDetail'));
const OpportunityPipeline = lazy(() => import('@/pages/opportunities/OpportunityPipeline'));
const Settings = lazy(() => import('@/pages/settings/Settings'));
const Reports = lazy(() => import('@/pages/reports/Reports'));
const ComingSoon = lazy(() => import('@/pages/shared/ComingSoon'));
const OrderList = lazy(() => import('@/pages/crm/OrderList'));
const SupplierList = lazy(() => import('@/pages/crm/SupplierList'));
const RecruitmentList = lazy(() => import('@/pages/hr/RecruitmentList'));
const PaymentList = lazy(() => import('@/pages/finance/PaymentList'));
const RadarSequences = lazy(() => import('@/pages/radar/RadarSequences'));
const RadarAnalytics = lazy(() => import('@/pages/radar/RadarAnalytics'));

// Radar
const RadarDashboard = lazy(() => import('@/pages/radar/RadarDashboard'));
const RadarSearch = lazy(() => import('@/pages/radar/RadarSearch'));
const ProspectList = lazy(() => import('@/pages/radar/ProspectList'));
const ProspectDetail = lazy(() => import('@/pages/radar/ProspectDetail'));
const RadarPipeline = lazy(() => import('@/pages/radar/RadarPipeline'));
const RadarInbox = lazy(() => import('@/pages/radar/RadarInbox'));

// Pulse
const PulseDashboard = lazy(() => import('@/pages/pulse/PulseDashboard'));
const PulseProjects = lazy(() => import('@/pages/pulse/PulseProjects'));
const PulseProjectNew = lazy(() => import('@/pages/pulse/PulseProjectNew'));
const PulseProjectDetail = lazy(() => import('@/pages/pulse/PulseProjectDetail'));
const PulseMyTasks = lazy(() => import('@/pages/pulse/PulseMyTasks'));
const PulseTimesheets = lazy(() => import('@/pages/pulse/PulseTimesheets'));
const PulseTrash = lazy(() => import('@/pages/pulse/PulseTrash'));

// Studio
const StudioDashboard = lazy(() => import('@/pages/studio/StudioDashboard'));
const StudioContents = lazy(() => import('@/pages/studio/StudioContents'));
const StudioContentNew = lazy(() => import('@/pages/studio/StudioContentNew'));
const StudioEditor = lazy(() => import('@/pages/studio/StudioEditor'));
const StudioBatch = lazy(() => import('@/pages/studio/StudioBatch'));
const StudioPlan = lazy(() => import('@/pages/studio/StudioPlan'));
const StudioWordPress = lazy(() => import('@/pages/studio/StudioWordPress'));

// Insights
const InsightsDashboard = lazy(() => import('@/pages/insights/InsightsDashboard'));
const InsightsReports = lazy(() => import('@/pages/insights/InsightsReports'));
const InsightsGenerate = lazy(() => import('@/pages/insights/InsightsGenerate'));
const InsightsConfig = lazy(() => import('@/pages/insights/InsightsConfig'));

// Flow
const FlowDashboard = lazy(() => import('@/pages/flow/FlowDashboard'));
const FlowPosts = lazy(() => import('@/pages/flow/FlowPosts'));
const FlowPostNew = lazy(() => import('@/pages/flow/FlowPostNew'));
const FlowCalendar = lazy(() => import('@/pages/flow/FlowCalendar'));
const FlowAccounts = lazy(() => import('@/pages/flow/FlowAccounts'));
const FlowStats = lazy(() => import('@/pages/flow/FlowStats'));

// Cockpit
const CockpitDashboard = lazy(() => import('@/pages/cockpit/CockpitDashboard'));
const CockpitProfitability = lazy(() => import('@/pages/cockpit/CockpitProfitability'));
const CockpitCashflow = lazy(() => import('@/pages/cockpit/CockpitCashflow'));
const CockpitTeam = lazy(() => import('@/pages/cockpit/CockpitTeam'));
const CockpitPipeline = lazy(() => import('@/pages/cockpit/CockpitPipeline'));
const CockpitAssistant = lazy(() => import('@/pages/cockpit/CockpitAssistant'));
const CockpitSettings = lazy(() => import('@/pages/cockpit/CockpitSettings'));

// Portal
const PortalLogin = lazy(() => import('@/pages/portal/PortalLogin'));
const PortalRegister = lazy(() => import('@/pages/portal/PortalRegister'));
const PortalDashboard = lazy(() => import('@/pages/portal/PortalDashboard'));
const PortalProjects = lazy(() => import('@/pages/portal/PortalProjects'));
const PortalProjectDetail = lazy(() => import('@/pages/portal/PortalProjectDetail'));
const PortalInvoices = lazy(() => import('@/pages/portal/PortalInvoices'));
const PortalTickets = lazy(() => import('@/pages/portal/PortalTickets'));
const PortalTicketDetail = lazy(() => import('@/pages/portal/PortalTicketDetail'));
const PortalMessages = lazy(() => import('@/pages/portal/PortalMessages'));
const PortalConversation = lazy(() => import('@/pages/portal/PortalConversation'));
const PortalReports = lazy(() => import('@/pages/portal/PortalReports'));
const PortalProfile = lazy(() => import('@/pages/portal/PortalProfile'));

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <Loading withOverlay={false} description="Chargement…" />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* ── Portal Client ── */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/register" element={<PortalRegister />} />
            <Route element={<PortalGuard />}>
              <Route element={<PortalLayout />}>
                <Route path="/portal" element={<PortalDashboard />} />
                <Route path="/portal/projects" element={<PortalProjects />} />
                <Route path="/portal/projects/:id" element={<PortalProjectDetail />} />
                <Route path="/portal/invoices" element={<PortalInvoices />} />
                <Route path="/portal/tickets" element={<PortalTickets />} />
                <Route path="/portal/tickets/:id" element={<PortalTicketDetail />} />
                <Route path="/portal/messages" element={<PortalMessages />} />
                <Route path="/portal/messages/:id" element={<PortalConversation />} />
                <Route path="/portal/reports" element={<PortalReports />} />
                <Route path="/portal/profile" element={<PortalProfile />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="/contacts" element={<ContactList />} />
                <Route path="/contacts/:id" element={<ContactDetail />} />
                <Route path="/quotes" element={<QuoteList />} />
                <Route path="/quotes/new" element={<QuoteForm />} />
                <Route path="/quotes/:id" element={<QuoteDetail />} />
                <Route path="/invoices" element={<InvoiceList />} />
                <Route path="/invoices/new" element={<ComingSoon module="Nouvelle facture" />} />
                <Route path="/invoices/:id" element={<InvoiceDetail />} />
                <Route path="/payments" element={<PaymentList />} />
                <Route path="/payroll" element={<PayrollList />} />
                <Route path="/banks" element={<BankList />} />
                <Route path="/banks/:id" element={<BankDetail />} />
                <Route path="/employees" element={<EmployeeList />} />
                <Route path="/employees/:id" element={<EmployeeDetail />} />
                <Route path="/leaves" element={<LeaveList />} />
                <Route path="/timesheets" element={<TimesheetWeek />} />
                <Route path="/recruitment" element={<RecruitmentList />} />
                <Route path="/orders" element={<OrderList />} />
                <Route path="/contracts" element={<ContractList />} />
                <Route path="/contracts/:id" element={<ContractDetail />} />
                <Route path="/opportunities" element={<OpportunityPipeline />} />
                <Route path="/contracts-old" element={<ComingSoon module="Contrats" />} />
                <Route path="/helpdesk" element={<TicketList />} />
                <Route path="/helpdesk/:id" element={<TicketDetail />} />
                <Route path="/suppliers" element={<SupplierList />} />
                <Route path="/projects" element={<ProjectList />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/ai-assistant" element={<AIAssistant />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/reports" element={<Reports />} />
                {/* Radar */}
                <Route path="/radar" element={<RadarDashboard />} />
                <Route path="/radar/search" element={<RadarSearch />} />
                <Route path="/radar/prospects" element={<ProspectList />} />
                <Route path="/radar/prospects/:id" element={<ProspectDetail />} />
                <Route path="/radar/pipeline" element={<RadarPipeline />} />
                <Route path="/radar/sequences" element={<RadarSequences />} />
                <Route path="/radar/inbox" element={<RadarInbox />} />
                <Route path="/radar/analytics" element={<RadarAnalytics />} />
                {/* Pulse */}
                <Route path="/pulse" element={<PulseDashboard />} />
                <Route path="/pulse/projects" element={<PulseProjects />} />
                <Route path="/pulse/projects/new" element={<PulseProjectNew />} />
                <Route path="/pulse/projects/:id/*" element={<PulseProjectDetail />} />
                <Route path="/pulse/my-tasks" element={<PulseMyTasks />} />
                <Route path="/pulse/timesheets" element={<PulseTimesheets />} />
                <Route path="/pulse/trash" element={<PulseTrash />} />
                {/* Studio */}
                <Route path="/studio" element={<StudioDashboard />} />
                <Route path="/studio/contents" element={<StudioContents />} />
                <Route path="/studio/contents/new" element={<StudioContentNew />} />
                <Route path="/studio/contents/:id" element={<StudioEditor />} />
                <Route path="/studio/batch" element={<StudioBatch />} />
                <Route path="/studio/plan" element={<StudioPlan />} />
                <Route path="/studio/wordpress" element={<StudioWordPress />} />
                {/* Insights */}
                <Route path="/insights" element={<InsightsDashboard />} />
                <Route path="/insights/reports" element={<InsightsReports />} />
                <Route path="/insights/generate" element={<InsightsGenerate />} />
                <Route path="/insights/config" element={<InsightsConfig />} />
                {/* Flow */}
                <Route path="/flow" element={<FlowDashboard />} />
                <Route path="/flow/posts" element={<FlowPosts />} />
                <Route path="/flow/posts/new" element={<FlowPostNew />} />
                <Route path="/flow/calendar" element={<FlowCalendar />} />
                <Route path="/flow/accounts" element={<FlowAccounts />} />
                <Route path="/flow/stats" element={<FlowStats />} />
                {/* Cockpit — ADMIN/MANAGER uniquement */}
                <Route path="/cockpit"              element={<CockpitRoute element={<CockpitDashboard />} />} />
                <Route path="/cockpit/profitability" element={<CockpitRoute element={<CockpitProfitability />} />} />
                <Route path="/cockpit/cashflow"      element={<CockpitRoute element={<CockpitCashflow />} />} />
                <Route path="/cockpit/team"          element={<CockpitRoute element={<CockpitTeam />} />} />
                <Route path="/cockpit/pipeline"      element={<CockpitRoute element={<CockpitPipeline />} />} />
                <Route path="/cockpit/assistant"     element={<CockpitRoute element={<CockpitAssistant />} />} />
                <Route path="/cockpit/settings"      element={<CockpitRoute element={<CockpitSettings />} />} />
                <Route path="*" element={<ComingSoon />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
